import {
  extractApiErrorCode,
  extractApiErrorMessage,
  extractApiValidationErrors,
} from '@bayes-mes/shared/utils/api-error';

import { $t } from '#/locales';

interface NormalizeHttpErrorOptions {
  fallbackMessage?: string;
}

const TIMEOUT_PATTERN = /timeout|ECONNABORTED|exceeded/i;
const HTML_DOCUMENT_PATTERN = /<\s*(?:!doctype|html|head|body)\b/i;

function getResponsePayload(error: any): unknown {
  return error?.response?.data ?? error?.data ?? error;
}

function getResponseMessage(error: any): string {
  const message = extractApiErrorMessage(getResponsePayload(error));

  return HTML_DOCUMENT_PATTERN.test(message) ? '' : message;
}

function getPermissionDeniedMessage(payload: unknown): string {
  const data = (payload as any)?.data;
  const missingPermission = data?.missingPermission;

  if (!missingPermission) {
    return '';
  }

  const permissionLabel = data?.permissionLabel || missingPermission;
  return $t('common.httpError.missingPermission', {
    missingPermission,
    permissionLabel,
  });
}

function getRetryAfterText(error: any): string {
  const headers = error?.response?.headers;
  const retryAfter =
    headers?.['retry-after'] ??
    headers?.['Retry-After'] ??
    headers?.get?.('Retry-After');
  const seconds = Number(retryAfter);
  return Number.isFinite(seconds) && seconds > 0
    ? $t('common.httpError.retryAfterSeconds', { seconds })
    : '';
}

export function normalizeHttpError(
  error: any,
  options: NormalizeHttpErrorOptions = {},
): string {
  const statusCode = error?.response?.status;
  const responsePayload = getResponsePayload(error);
  const responseMessage = getResponseMessage(error);
  const validationErrors = extractApiValidationErrors(responsePayload);
  const errorCode = extractApiErrorCode(responsePayload);

  let friendlyMessage = '';

  switch (statusCode) {
    case 400: {
      friendlyMessage = responseMessage || $t('common.httpError.badRequest');
      break;
    }
    case 401: {
      friendlyMessage = responseMessage || $t('common.httpError.unauthorized');
      break;
    }
    case 403: {
      friendlyMessage =
        getPermissionDeniedMessage(responsePayload) ||
        responseMessage ||
        $t('common.httpError.forbidden');
      break;
    }
    case 404: {
      friendlyMessage = responseMessage || $t('common.httpError.notFound');
      break;
    }
    case 408: {
      friendlyMessage = responseMessage || $t('common.httpError.timeout');
      break;
    }
    case 422: {
      friendlyMessage =
        responseMessage || $t('common.httpError.validationFailed');
      break;
    }
    case 429: {
      friendlyMessage =
        responseMessage ||
        $t('common.httpError.tooManyAttempts', {
          retryAfter: getRetryAfterText(error),
        });
      break;
    }
    case 500: {
      friendlyMessage = responseMessage || $t('common.httpError.serverError');
      break;
    }
    case 502: {
      friendlyMessage = responseMessage || $t('common.httpError.badGateway');
      break;
    }
    case 503: {
      friendlyMessage =
        responseMessage || $t('common.httpError.serviceUnavailable');
      break;
    }
    case 504: {
      friendlyMessage =
        responseMessage || $t('common.httpError.gatewayTimeout');
      break;
    }
    default: {
      if (error?.code === 'ERR_NETWORK') {
        friendlyMessage = $t('common.httpError.networkError');
      } else if (
        error?.code === 'ECONNABORTED' ||
        TIMEOUT_PATTERN.test(String(error?.message ?? ''))
      ) {
        friendlyMessage = $t('common.httpError.timeout');
      } else {
        friendlyMessage =
          responseMessage ||
          options.fallbackMessage ||
          $t('common.httpError.requestFailed');
      }
    }
  }

  try {
    error.friendlyMessage = friendlyMessage;
    error.errorCode = errorCode;
    error.message = friendlyMessage;
    error.validationErrors = validationErrors;
  } catch {
    // ignore readonly error objects
  }

  return friendlyMessage;
}

export function getHttpErrorMessage(
  error: unknown,
  fallbackMessage = $t('common.httpError.requestFailed'),
): string {
  if (typeof error === 'string') {
    return error.trim() || fallbackMessage;
  }

  const candidate = error as any;
  return (
    candidate?.friendlyMessage ||
    extractApiErrorMessage(getResponsePayload(candidate)) ||
    candidate?.message ||
    fallbackMessage
  );
}
